import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { AlertTriangle, Plus, CheckCircle2, X } from 'lucide-react';
import { getPhaseChecklist, getNextPhaseName } from '@/lib/phaseChecklists';

/**
 * Phase Transition Checklist Modal
 * Shows when a phase reaches 75% completion to help users verify critical items
 */
export const PhaseChecklistModal = ({ 
  isOpen, 
  onClose, 
  phaseNumber, 
  phaseName,
  onComplete,
  onSkip 
}) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [customItems, setCustomItems] = useState([]);
  const [newItemText, setNewItemText] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  
  const checklist = getPhaseChecklist(phaseNumber);
  const nextPhaseName = getNextPhaseName(phaseNumber);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCheckedItems({});
      setCustomItems([]);
      setNewItemText('');
      setShowAddItem(false);
    }
  }, [isOpen, phaseNumber]);
  
  if (!checklist) return null;
  
  const allItems = [...checklist.items, ...customItems];
  const requiredItems = checklist.items.filter(item => item.required);
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const allRequiredChecked = requiredItems.every(item => checkedItems[item.id]);
  const allChecked = allItems.every(item => checkedItems[item.id]);
  
  const handleToggleItem = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  const handleAddCustomItem = () => {
    if (!newItemText.trim()) return;
    
    const newItem = {
      id: `custom-${Date.now()}`,
      text: newItemText.trim(),
      required: false,
      isCustom: true
    };
    
    setCustomItems(prev => [...prev, newItem]);
    setNewItemText('');
    setShowAddItem(false);
  };
  
  const handleRemoveCustomItem = (itemId) => {
    setCustomItems(prev => prev.filter(item => item.id !== itemId));
    setCheckedItems(prev => {
      const newChecked = { ...prev };
      delete newChecked[itemId];
      return newChecked;
    });
  };
  
  const handleComplete = () => {
    onComplete?.();
    onClose();
  };
  
  const handleSkip = () => {
    toast.warning(
      `Skipped phase checklist for ${phaseName}. Some items may still need attention.`,
      { duration: 5000 }
    );
    onSkip?.();
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] flex flex-col" data-testid="phase-checklist-modal">
        <DialogHeader className="flex-shrink-0 border-b pb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <DialogTitle className="text-lg">
                {checklist.title}
              </DialogTitle>
              <DialogDescription className="text-sm">
                Phase {phaseNumber}: {phaseName} → {nextPhaseName}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-4">
          <p className="text-sm text-slate-600 mb-4">
            {checklist.subtitle}
          </p>
          
          {/* Progress indicator */}
          <div className="mb-4 bg-slate-50 rounded-lg p-3">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-600">Checklist Progress</span>
              <span className="font-medium">{checkedCount} / {allItems.length} items</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${allItems.length > 0 ? (checkedCount / allItems.length) * 100 : 0}%` }}
              />
            </div>
          </div>
          
          {/* Checklist items */}
          <div className="space-y-2">
            {allItems.map((item) => (
              <div 
                key={item.id}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                  checkedItems[item.id] 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <Checkbox
                  id={item.id}
                  checked={checkedItems[item.id] || false}
                  onCheckedChange={() => handleToggleItem(item.id)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <label 
                    htmlFor={item.id}
                    className={`text-sm cursor-pointer ${
                      checkedItems[item.id] ? 'text-green-700 line-through' : 'text-slate-700'
                    }`}
                  >
                    {item.text}
                  </label>
                  {item.required && !item.isCustom && (
                    <span className="ml-2 text-xs text-red-500 font-medium">Required</span>
                  )}
                </div>
                {item.isCustom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveCustomItem(item.id)}
                    className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          
          {/* Add custom item */}
          {showAddItem ? (
            <div className="mt-3 flex gap-2">
              <Input
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                placeholder="Enter custom checklist item..."
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddCustomItem();
                  if (e.key === 'Escape') setShowAddItem(false);
                }}
                autoFocus
              />
              <Button size="sm" onClick={handleAddCustomItem} disabled={!newItemText.trim()}>
                Add
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAddItem(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="link"
              size="sm"
              onClick={() => setShowAddItem(true)}
              className="mt-3 text-blue-600 hover:text-blue-700 p-0 h-auto"
              data-testid="add-custom-item-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Custom Checklist Item
            </Button>
          )}
        </div>
        
        {/* Warning banner */}
        <div className="flex-shrink-0 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>Warning:</strong> Skipping these items may cause delays in the next phase. 
              Items behind walls cannot be easily accessed after drywall installation.
            </p>
          </div>
        </div>
        
        <DialogFooter className="flex-shrink-0 border-t pt-4 gap-2">
          <Button
            variant="outline"
            onClick={handleSkip}
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
            data-testid="skip-with-warning-btn"
          >
            Skip with Warning
          </Button>
          <Button
            onClick={handleComplete}
            disabled={!allRequiredChecked}
            className="bg-green-600 hover:bg-green-700"
            data-testid="mark-complete-btn"
          >
            {allChecked ? 'Mark Complete' : `Complete (${checkedCount}/${allItems.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhaseChecklistModal;
